/**
 * The cards above the queue.
 *
 * Legacy's analytics strip, and its best idea: a card is also a filter, so
 * "twelve in review" is one click from being the twelve. Pressing the card that
 * is already on takes the filter off again, which is the behaviour it had and
 * the thing that makes them usable as a set.
 *
 * The figures are the whole desk's, not the current filter's — legacy held them
 * still for the same reason: a card that followed the filter would read zero on
 * every other status the moment one was pressed.
 *
 * Compact, and every card whole. They began as a wrapping grid of tall cards
 * that stood three rows deep on an ordinary window and pushed the table off the
 * bottom of a screen with no scrollbar to reach it by; they then spent an hour
 * as a single row that scrolled sideways, which fitted but cut the last card in
 * half down the right-hand edge — a number sliced vertically reads as a broken
 * page, not as an invitation to scroll.
 *
 * So: short cards that wrap, and a floor under the table below them
 * (`useTableFloor`) so what they do cost comes out of a page that scrolls
 * rather than out of the rows. Nothing is cropped, and the strip is two rows at
 * its worst instead of four.
 */

import { useQuery } from "@tanstack/react-query";

import { Skeleton } from "@/components/ui/skeleton";
import type { TranslationKey } from "@/constants/i18n";
import {
  STATUS_LABEL,
  type TicketCounts,
  type TicketStatus,
  ticketSummaryQuery,
} from "@/services/helpdesk";
import { useTranslation } from "@/store/locale";
import { cn } from "@/utils/utils";

/** Which count each card reads, and which status it filters to. */
const CARDS: readonly {
  key: keyof TicketCounts;
  status: TicketStatus | null;
  label?: TranslationKey;
}[] = [
  { key: "total", status: null, label: "hd.cardTotal" },
  { key: "new", status: "new" },
  { key: "in_progress", status: "in_progress" },
  { key: "in_review", status: "in_review" },
  { key: "reopen", status: "reopen" },
  { key: "resolved", status: "resolved" },
  { key: "rejected", status: "rejected" },
];

/** One card: a word, a number, and the filter it stands for. */
const CARD_CLASS =
  // `grow` off a small basis so a line of them divides the width evenly, and a
  // ceiling so the last one on a line of its own does not take the whole of it:
  // Overdue, alone on the second row, was one number stretched across the page
  // and read as a banner rather than as the eighth of eight cards.
  "rounded-card flex min-w-[6.5rem] max-w-[12rem] grow basis-[6.5rem] flex-col items-start " +
  "gap-0 px-3 py-1.5 text-left " +
  "transition-[background-color,box-shadow] duration-[var(--duration-fast)]";

const STRIP_CLASS =
  // Wrapping, not scrolling: a card half off the edge is a bug people report.
  // `shrink-0` keeps the strip out of the fight for height, so what gives way
  // under a short window is the table — which has a floor and a page that
  // scrolls — and never a card's own label.
  "mb-4 flex shrink-0 flex-wrap gap-2 sm:gap-2.5";

export function TicketSummary({
  active,
  overdue = false,
  onPick,
  onPickOverdue,
}: {
  /** The status the list is filtered to, if any. */
  active: TicketStatus | null;
  /** Whether the list is showing only what is past its due date. */
  overdue?: boolean;
  onPick: (status: TicketStatus | null) => void;
  onPickOverdue?: (() => void) | undefined;
}) {
  const t = useTranslation();
  const summary = useQuery(ticketSummaryQuery());

  if (summary.isLoading) {
    return (
      <div className={STRIP_CLASS}>
        {CARDS.map((card) => (
          <Skeleton key={card.key} className={cn(CARD_CLASS, "h-[52px]")} />
        ))}
      </div>
    );
  }
  // A deployment that cannot answer, or a person who may not read it, gets the
  // list without the cards rather than a row of error boxes over it.
  if (!summary.data) return null;

  const counts = summary.data;

  return (
    <div className={STRIP_CLASS}>
      {CARDS.map((card) => {
        const on = card.status === active && !overdue;
        const label = t(card.label ?? STATUS_LABEL[card.status!]);
        return (
          <button
            key={card.key}
            type="button"
            aria-pressed={on}
            onClick={() => onPick(on ? null : card.status)}
            className={cn(
              CARD_CLASS,
              on
                ? "bg-accent-soft ring-accent ring-1"
                : "bg-surface-raised hover:bg-surface-sunken",
            )}
          >
            <span
              title={label}
              className={cn(
                "text-footnote w-full truncate",
                on ? "text-accent" : "text-fg-muted",
              )}
            >
              {label}
            </span>
            <span className={cn("text-title-3", on ? "text-accent" : "text-fg")}>
              {counts[card.key].toLocaleString()}
            </span>
          </button>
        );
      })}

      {/* Overdue is not a status, so it cannot be one of the seven — but it is
          the number a desk actually runs on, and it used to say so in a line of
          its own under the cards, which cost a row of height to tell one fact.
          It is a card at the end of the strip instead, in the warning colour so
          it does not read as an eighth status, and pressing it filters the list
          to exactly what it counts. */}
      {onPickOverdue ? (
        <button
          type="button"
          aria-pressed={overdue}
          onClick={onPickOverdue}
          className={cn(
            CARD_CLASS,
            overdue
              ? "bg-warning-soft ring-warning ring-1"
              : "bg-surface-raised hover:bg-surface-sunken",
          )}
        >
          <span
            title={t("hd.cardOverdue")}
            className={cn(
              "text-footnote w-full truncate",
              overdue ? "text-warning" : "text-fg-muted",
            )}
          >
            {t("hd.cardOverdue")}
          </span>
          <span
            className={cn(
              "text-title-3",
              overdue || counts.overdue > 0 ? "text-warning" : "text-fg",
            )}
          >
            {counts.overdue.toLocaleString()}
          </span>
        </button>
      ) : null}
    </div>
  );
}
